import React, { createContext, useReducer, useCallback, useMemo } from 'react';
import { M3uMedia } from '../../lib/m3u';

const MAX_HISTORY_SIZE = 50;

interface ChannelHistoryState {
    history: M3uMedia[];
    currentIndex: number;
}

interface ChannelHistoryContextType {
    canGoBack: boolean;
    canGoForward: boolean;
    goBack: () => M3uMedia | null;
    goForward: () => M3uMedia | null;
    addToHistory: (channel: M3uMedia) => void;
}

type HistoryAction =
    | { type: 'ADD'; channel: M3uMedia }
    | { type: 'GO_BACK' }
    | { type: 'GO_FORWARD' };

const initialState: ChannelHistoryState = {
    history: [],
    currentIndex: -1,
};

const initialContextValue: ChannelHistoryContextType = {
    canGoBack: false,
    canGoForward: false,
    goBack: () => null,
    goForward: () => null,
    addToHistory: () => {},
};

function historyReducer(state: ChannelHistoryState, action: HistoryAction): ChannelHistoryState {
    switch (action.type) {
        case 'ADD': {
            const { channel } = action;
            const { history, currentIndex } = state;

            // Check if this channel is the same as current (by location)
            if (currentIndex >= 0 && history[currentIndex]?.location === channel.location) {
                return state;
            }

            // Truncate any forward history when adding new channel
            const newHistory = history.slice(0, currentIndex + 1);
            newHistory.push(channel);

            // Limit history size
            if (newHistory.length > MAX_HISTORY_SIZE) {
                newHistory.shift();
                return {
                    history: newHistory,
                    currentIndex: newHistory.length - 1,
                };
            }

            return {
                history: newHistory,
                currentIndex: newHistory.length - 1,
            };
        }
        case 'GO_BACK': {
            if (state.currentIndex > 0) {
                return {
                    ...state,
                    currentIndex: state.currentIndex - 1,
                };
            }
            return state;
        }
        case 'GO_FORWARD': {
            if (state.currentIndex < state.history.length - 1) {
                return {
                    ...state,
                    currentIndex: state.currentIndex + 1,
                };
            }
            return state;
        }
        default:
            return state;
    }
}

export const ChannelHistoryContext = createContext<ChannelHistoryContextType>(initialContextValue);

export const ChannelHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(historyReducer, initialState);

    const canGoBack = state.currentIndex > 0;
    const canGoForward = state.currentIndex < state.history.length - 1;

    const goBack = useCallback((): M3uMedia | null => {
        if (state.currentIndex > 0) {
            dispatch({ type: 'GO_BACK' });
            return state.history[state.currentIndex - 1];
        }
        return null;
    }, [state.currentIndex, state.history]);

    const goForward = useCallback((): M3uMedia | null => {
        if (state.currentIndex < state.history.length - 1) {
            dispatch({ type: 'GO_FORWARD' });
            return state.history[state.currentIndex + 1];
        }
        return null;
    }, [state.currentIndex, state.history]);

    const addToHistory = useCallback((channel: M3uMedia): void => {
        dispatch({ type: 'ADD', channel });
    }, []);

    const contextValue = useMemo(() => ({
        canGoBack,
        canGoForward,
        goBack,
        goForward,
        addToHistory,
    }), [canGoBack, canGoForward, goBack, goForward, addToHistory]);

    return (
        <ChannelHistoryContext.Provider value={contextValue}>
            {children}
        </ChannelHistoryContext.Provider>
    );
};

export default ChannelHistoryProvider;
